import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(() => localStorage.getItem('workspace') || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      const nextUser = response.data.user;
      setUser(nextUser);
      if (nextUser?.role === 'tech' || nextUser?.role === 'tech_admin') {
        localStorage.setItem('workspace', 'tech');
        setWorkspace('tech');
      } else if (!localStorage.getItem('workspace')) {
        localStorage.setItem('workspace', 'business');
        setWorkspace('business');
      }
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('workspace');
      delete api.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('workspace', 'business');
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setWorkspace('business');
    setUser(user);
    return user;
  };

  const loginTech = async (email, password) => {
    const response = await api.post('/auth/tech-login', { email, password });
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('workspace', 'tech');
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setWorkspace('tech');
    setUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('workspace');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setWorkspace(null);
  };

  const isTechWorkspace = user?.role === 'tech' || user?.role === 'tech_admin';

  const value = {
    user,
    loading,
    login,
    loginTech,
    logout,
    workspace,
    isAdmin: user?.role === 'admin',
    isTech: user?.role === 'tech',
    isTechAdmin: user?.role === 'tech_admin',
    isTechWorkspace,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
